import React, { useCallback, memo, useMemo } from "react";
import { Box, RadioGroup, useToast } from "@chakra-ui/react";
import { useIntl } from "react-intl";

import { walletHasEnoughToCoverAmount } from "utils";
import { isWallet } from "components/PaymentMethods/common";
import WalletBalanceToggle from "components/WalletBalanceToggle";
import {
  PaymentMethodItem,
  SelectButton,
  getFinalPaymentMethods,
  sortPaymentMethods,
} from "components/PaymentMethods";
import { useFlowConfig } from "flows/hooks";

const MultiPaymentMethods = ({
  paymentMethods = [],
  transactionDetails = {},
  chooserConfig = {},
  balances = {},
  submitLabel = "Use this payment method",
  selectedPaymentMethods = [],
  setSelectedPaymentMethods,
  onSubmit,
  submitIsDisabled = false,
  onPaymentMethodUpdated,
  showMenu = true,
}) => {
  const { formatMessage } = useIntl();
  const toast = useToast();
  const flowConfig = useFlowConfig();

  const { useWalletBalance, selectedPaymentMethodId } = useMemo(() => {
    let useWalletBalance = false;
    let selectedPaymentMethodId = "";

    if (selectedPaymentMethods.length > 0) {
      // Find the selected payment method id, if any
      const selectedPaymentMethod = selectedPaymentMethods.find((p) => !p.isWallet);
      selectedPaymentMethodId = selectedPaymentMethod?.payment_method_id || "";

      // Find the toggled wallet payment method id, if any
      const walletPaymentMethod = selectedPaymentMethods.find((p) => p.isWallet);
      useWalletBalance = Boolean(walletPaymentMethod);
    }

    return { useWalletBalance, selectedPaymentMethodId };
  }, [selectedPaymentMethods]);

  const { listOfPaymentMethods, wallet, walletCoversAmount, walletUsedAndCoversAmount } =
    useMemo(() => {
      const filteredPaymentMethods = getFinalPaymentMethods(
        paymentMethods,
        chooserConfig,
        flowConfig
      );
      const sortedFilteredPaymentMethods = sortPaymentMethods(filteredPaymentMethods);

      let listOfPaymentMethods = [...sortedFilteredPaymentMethods];
      let wallet = null;
      let walletCoversAmount = false;
      let walletUsedAndCoversAmount = false;

      const { transaction_amount, currency } = transactionDetails;
      // If we get transaction details information, we check to see if we can show the user their wallet balance as a toggle
      if (transaction_amount && currency) {
        // Filter out wallet payment methods
        const nonWalletPaymentMethods = [
          ...sortedFilteredPaymentMethods.filter((p) => p.provider !== "wallet"),
        ];

        // If we have a payment method that isn't a wallet, we continue. If the end-user is only being shown their wallet,
        // then we don't want to show the toggle, but just show the wallet as a single payment method item to pick from
        if (nonWalletPaymentMethods.length > 0) {
          // Update the final list of payment methods being shown to only be non-wallet payment methods
          listOfPaymentMethods = [...nonWalletPaymentMethods];
          // Filter out non-wallet payment methods
          const walletPaymentMethod = sortedFilteredPaymentMethods.find(
            (p) => p.provider === "wallet" && p.processing_currency === currency.toUpperCase()
          );
          // Check if we have at least one wallet and balances being returned for the given currency
          if (Boolean(walletPaymentMethod) && balances) {
            const walletBalanceInfo = balances[currency.toUpperCase()];
            wallet = {
              payment_method_id: walletPaymentMethod.payment_method_id,
              ...walletBalanceInfo,
            };
            walletCoversAmount = walletHasEnoughToCoverAmount(wallet, transaction_amount);
            walletUsedAndCoversAmount = useWalletBalance && walletCoversAmount;
          }
        }
      }

      return { listOfPaymentMethods, wallet, walletCoversAmount, walletUsedAndCoversAmount };
    }, [balances, chooserConfig, flowConfig, paymentMethods, transactionDetails, useWalletBalance]);

  const handleWalletChecked = useCallback(
    (isChecked) => {
      setSelectedPaymentMethods((currentSelectedPaymentMethods) => {
        let newSelectedPaymentMethods = [...currentSelectedPaymentMethods];
        if (isChecked) {
          if (walletCoversAmount) {
            // If the user toggles their wallet balance on, and their wallet covers the transaction amount,
            // then we clear out the currently selected payment method here (if any) and only leave the wallet
            newSelectedPaymentMethods = [
              {
                payment_method_id: wallet.payment_method_id,
                amount: 0,
                isWallet: true,
              },
            ];
          } else {
            newSelectedPaymentMethods.push({
              payment_method_id: wallet.payment_method_id,
              amount: wallet.spendable.balance,
              isWallet: true,
            });
          }
        } else {
          newSelectedPaymentMethods = newSelectedPaymentMethods.filter((p) => !p.isWallet);
        }

        return newSelectedPaymentMethods;
      });
    },
    [setSelectedPaymentMethods, wallet, walletCoversAmount]
  );

  const handlePaymentMethodSelected = useCallback(
    (paymentMethodId) => {
      setSelectedPaymentMethods((currentSelectedPaymentMethods) => {
        const out = [
          {
            payment_method_id: paymentMethodId,
            amount: 0,
            isWallet: false,
          },
        ];

        // If we've already got wallet balance toggled on, we need to change the amount
        // for the wallet payment method to be the balance of the wallet instead of 0
        const toggledWallet = currentSelectedPaymentMethods.find((p) => p.isWallet);
        if (toggledWallet) {
          out.push({
            ...toggledWallet,
            amount: wallet.spendable.balance,
          });
        }

        return out;
      });
    },
    [setSelectedPaymentMethods, wallet]
  );

  const handleSubmit = useCallback(() => {
    if (selectedPaymentMethods.length <= 0) {
      toast({
        title: formatMessage({
          defaultMessage: "No payment method selected",
          description: "Pop up title",
        }),
        description: formatMessage({
          defaultMessage: "Please select a payment method before submitting.",
          description: "Pop up description",
        }),
        status: "error",
        isClosable: true,
      });

      return;
    }

    onSubmit();
  }, [onSubmit, selectedPaymentMethods.length, toast, formatMessage]);

  const canSubmit = Boolean(selectedPaymentMethodId) || walletUsedAndCoversAmount;

  return (
    <Box>
      {wallet?.spendable ? (
        <WalletBalanceToggle
          wallet={wallet}
          isChecked={useWalletBalance}
          setChecked={handleWalletChecked}
          // isDisabled={!walletCoversAmount && listOfPaymentMethods.length <= 0}
        />
      ) : null}

      <RadioGroup
        onChange={handlePaymentMethodSelected}
        value={selectedPaymentMethodId}
        aria-label="List of your available payment methods"
      >
        {listOfPaymentMethods.map((pm) => (
          <PaymentMethodItem
            key={`payment-method-item-${pm.id}`}
            data={pm}
            isDisabled={
              walletUsedAndCoversAmount ||
              (isWallet(pm) &&
                transactionDetails?.transaction_amount &&
                pm?.provider_data?.balance < transactionDetails?.transaction_amount)
            }
            showMenu={showMenu && !isWallet(pm)}
            onComplete={onPaymentMethodUpdated}
          />
        ))}
      </RadioGroup>

      <Box mt={4} mb={8}>
        <SelectButton
          label={submitLabel}
          chooserConfig={chooserConfig}
          flowConfig={flowConfig}
          disabled={!canSubmit || submitIsDisabled}
          onClick={handleSubmit}
        />
      </Box>
    </Box>
  );
};

export default memo(MultiPaymentMethods);
